import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './AnalyticsDashboardPage.css';

interface DashboardStats {
  totalTestCases: number;
  totalScenarios: number;
  totalProjects: number;
  totalRuns: number;
  completedRuns: number;
  passCount: number;
  failCount: number;
  blockedCount: number;
  successRate: number;
}

interface ActivityRow {
  sort_key: string;
  period: string;
  user: string;
  // Overall / By User view
  tc_created?: number;
  tc_executed?: number;
  sc_created?: number;
  sc_executed?: number;
  runs_created?: number;
  runs_completed?: number;
  // AI vs Manual view
  tc_ai?: number;
  tc_manual?: number;
  tc_ai_pct?: number;
  tc_manual_pct?: number;
  sc_ai?: number;
  sc_manual?: number;
  sc_ai_pct?: number;
  sc_manual_pct?: number;
}

const PRIORITY_COLORS: Record<string, string> = {
  Critical: '#ef4444',
  High: '#f97316',
  Medium: '#3b82f6',
  Low: '#9ca3af',
};

export const AnalyticsDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [stats, setStats] = useState<DashboardStats>({
    totalTestCases: 0, totalScenarios: 0, totalProjects: 0,
    totalRuns: 0, completedRuns: 0,
    passCount: 0, failCount: 0, blockedCount: 0, successRate: 0,
  });
  const [recentRuns, setRecentRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Activity report state
  const [actPeriod, setActPeriod] = useState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [actView, setActView] = useState<'overall' | 'by_user' | 'ai_vs_manual'>('overall');
  const [actMonths, setActMonths] = useState(12);
  const [actRows, setActRows] = useState<ActivityRow[]>([]);
  const [actUsers, setActUsers] = useState<string[]>([]);
  const [actUserFilter, setActUserFilter] = useState<string>('');
  const [actLoading, setActLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Global date range filter
  const DATE_RANGES = [
    { label: 'Last 30 Days', days: 30 },
    { label: '1 Month',      days: 30 },
    { label: '3 Months',     days: 90 },
    { label: '6 Months',     days: 180 },
    { label: '1 Year',       days: 365 },
  ];
  const [dateRangeDays, setDateRangeDays] = useState(30);
  const [dateRangeLabel, setDateRangeLabel] = useState('Last 30 Days');
  const [showDateDropdown, setShowDateDropdown] = useState(false);

  useEffect(() => { loadDashboard(); }, [dateRangeDays]); // eslint-disable-line

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.get(`/api/v1/dashboard/stats?days=${dateRangeDays}`);
      const data = resp.data as any;
      const kpis = data.kpis;
      const exec = data.execution_summary;
      setStats({
        totalTestCases: kpis.total_test_cases,
        totalScenarios: kpis.total_scenarios,
        totalProjects: kpis.total_projects,
        totalRuns: kpis.total_runs,
        completedRuns: kpis.completed_runs,
        passCount: exec.pass_count,
        failCount: exec.fail_count,
        blockedCount: exec.blocked_count,
        successRate: exec.success_rate,
      });
      setRecentRuns(data.recent_runs || []);
    } catch (e) {
      console.error('Dashboard load error', e);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityReport = async () => {
    setActLoading(true);
    try {
      const resp = await apiClient.get(
        `/api/v1/dashboard/activity-report?period=${actPeriod}&view=${actView}&months_back=${actMonths}`
      );
      const data = resp.data as any;
      setActRows(data.rows || []);
      setActUsers(data.users || []);
      setActUserFilter('');
    } catch (e) {
      console.error('Activity report error', e);
      setActRows([]);
      setActUsers([]);
    } finally {
      setActLoading(false);
    }
  };

  // Call loadActivityReport on mount and when dependencies change
  useEffect(() => {
    loadActivityReport();
  }, [actPeriod, actView, actMonths]);

  const handleExport = async () => {
    if (!isAdmin) return;
    setExportLoading(true);
    try {
      const authTokens = localStorage.getItem('auth_tokens');
      const tokens = authTokens ? JSON.parse(authTokens) : null;
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const resp = await fetch(
        `${apiBase}/api/v1/dashboard/export-report?period=${actPeriod}&view=${actView}&months_back=${actMonths}`,
        { headers: { Authorization: `Bearer ${tokens?.access_token}` } }
      );
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assurex_report_${actPeriod}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Export error', e);
    } finally {
      setExportLoading(false);
    }
  };

  const formatDuration = (run: any) => {
    if (!run.duration_seconds) return '—';
    const m = Math.floor(run.duration_seconds / 60);
    const s = run.duration_seconds % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return 'just now';
    if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
    const d = Math.floor(h / 24);
    return `${d} day${d > 1 ? 's' : ''} ago`;
  };

  const statusBadge = (status: string, pct?: number) => {
    if (status === 'COMPLETED') return <span className="run-badge badge-completed">COMPLETED</span>;
    if (status === 'IN_PROGRESS') return <span className="run-badge badge-inprogress">IN PROGRESS</span>;
    if (status === 'ABORTED') return <span className="run-badge badge-failed" style={{ color: pct && pct < 80 ? '#dc2626' : undefined }}>FAILED</span>;
    return <span className="run-badge badge-notstarted">NOT STARTED</span>;
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="dashboard-page">
      {/* Page header */}
      <div className="dash-header">
        <div>
          <h1 className="dash-title">AssureX Analytics</h1>
          <p className="dash-subtitle">Enterprise QA Performance &amp; Diagnostic Dashboard</p>
        </div>
        <div className="dash-header-actions">
          {/* Date range dropdown */}
          <div className="date-range-wrap" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowDateDropdown(false); }}>
            <button className="dash-btn-outline date-range-btn" onClick={() => setShowDateDropdown(v => !v)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              {dateRangeLabel}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            {showDateDropdown && (
              <div className="date-range-dropdown">
                {DATE_RANGES.map(opt => (
                  <button key={opt.label} className={`date-range-option${dateRangeLabel === opt.label ? ' selected' : ''}`}
                    onClick={() => { setDateRangeDays(opt.days); setDateRangeLabel(opt.label); setShowDateDropdown(false); }}>
                    {dateRangeLabel === opt.label && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className={`dash-btn-primary${!isAdmin ? ' dash-btn-disabled' : ''}`}
            onClick={handleExport} disabled={!isAdmin || exportLoading}
            title={isAdmin ? 'Export report as CSV' : 'Only Admin users can export reports'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {exportLoading ? 'Exporting...' : 'Export Data Report'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="dash-loading">Loading dashboard...</div>
      ) : (
        <>
          {/* KPI Cards — order: Total Test Cases, Failed Executions, Total Projects, Total Runs */}
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-blue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                  </svg>
                </div>
                <span className="kpi-delta delta-up">+12%</span>
              </div>
              <div className="kpi-label">Total Test Cases</div>
              <div className="kpi-value">{stats.totalTestCases.toLocaleString()}</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-green">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-label">Total Test Scenarios</div>
              <div className="kpi-value">{stats.totalScenarios.toLocaleString()}</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-purple">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                  </svg>
                </div>
                <span className="kpi-delta delta-neutral">STABLE</span>
              </div>
              <div className="kpi-label">Total Projects</div>
              <div className="kpi-value">{stats.totalProjects}</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-teal">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>
                  </svg>
                </div>
                <span className="kpi-delta delta-up">+{stats.totalRuns}</span>
              </div>
              <div className="kpi-label">Total Runs</div>
              <div className="kpi-value">{stats.totalRuns}</div>
            </div>
          </div>

          {/* Middle row: Success Rate donut + Execution Summary */}
          <div className="dash-mid-row">
            {/* Overall Success Rate — donut */}
            <div className="dash-card" style={{ minWidth: 260, maxWidth: 300 }}>
              <div className="dash-card-header">
                <div>
                  <h3>Overall Success Rate</h3>
                  <p>Performance breakdown by status</p>
                </div>
              </div>
              <div className="success-donut-wrap">
                {(() => {
                  const total = stats.passCount + stats.failCount + stats.blockedCount;
                  const pct = stats.successRate;
                  const r = 60;
                  const circ = 2 * Math.PI * r;
                  const passArc = total > 0 ? (stats.passCount / total) * circ : 0;
                  const failArc = total > 0 ? ((stats.failCount + stats.blockedCount) / total) * circ : 0;
                  return (
                    <div className="donut-container">
                      <svg className="donut-svg" width="160" height="160" viewBox="0 0 160 160">
                        <circle cx="80" cy="80" r={r} fill="none" stroke="#f3f4f6" strokeWidth="18"/>
                        {total > 0 && (
                          <>
                            <circle cx="80" cy="80" r={r} fill="none" stroke="#22c55e" strokeWidth="18"
                              strokeDasharray={`${passArc} ${circ - passArc}`} strokeLinecap="round"/>
                            <circle cx="80" cy="80" r={r} fill="none" stroke="#ef4444" strokeWidth="18"
                              strokeDasharray={`${failArc} ${circ - failArc}`}
                              strokeDashoffset={-passArc} strokeLinecap="round"/>
                          </>
                        )}
                      </svg>
                      <div className="donut-center">
                        <span className="donut-pct">{pct > 0 ? `${pct}%` : '—'}</span>
                        <span className="donut-label">SUCCESS</span>
                      </div>
                    </div>
                  );
                })()}
                <div className="donut-legend">
                  <div className="donut-legend-item">
                    <div className="donut-legend-left">
                      <span className="donut-dot" style={{ background: '#22c55e' }}/>
                      Passed
                    </div>
                    <span className="donut-legend-pct">{stats.successRate > 0 ? `${stats.successRate}%` : '—'}</span>
                  </div>
                  <div className="donut-legend-item">
                    <div className="donut-legend-left">
                      <span className="donut-dot" style={{ background: '#ef4444' }}/>
                      Failed/Blocked
                    </div>
                    <span className="donut-legend-pct">
                      {stats.successRate > 0 ? `${(100 - stats.successRate).toFixed(1)}%` : '—'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Test Execution Summary */}
            <div className="dash-card dash-card-wide">
              <div className="dash-card-header">
                <div>
                  <h3>Test Execution Summary</h3>
                  <p>Distribution of test results across all suites</p>
                </div>
                <div className="chart-legend">
                  <span className="legend-dot" style={{ background: '#22c55e' }}/> PASSED
                  <span className="legend-dot" style={{ background: '#f59e0b', marginLeft: 10 }}/> BLOCKED
                  <span className="legend-dot" style={{ background: '#ef4444', marginLeft: 10 }}/> FAILED
                </div>
              </div>
              <div className="exec-bars">
                {[
                  { label: 'Pass', count: stats.passCount, color: '#22c55e' },
                  { label: 'Blocked', count: stats.blockedCount, color: '#f59e0b' },
                  { label: 'Fail', count: stats.failCount, color: '#ef4444' },
                ].map(item => {
                  const total = stats.passCount + stats.failCount + stats.blockedCount;
                  const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  return (
                    <div key={item.label} className="exec-bar-row">
                      <span className="exec-bar-label">{item.label}</span>
                      <div className="exec-bar-track">
                        <div className="exec-bar-fill" style={{ width: `${pct}%`, background: item.color }}/>
                      </div>
                      <span className="exec-bar-count">{item.count.toLocaleString()}</span>
                      <span className="exec-bar-pct">{pct}%</span>
                    </div>
                  );
                })}
                {stats.passCount + stats.failCount + stats.blockedCount === 0 && (
                  <div className="exec-empty">No completed test runs yet. <Link to="/test-runs/create">Create a test run</Link> to see results here.</div>
                )}
              </div>
              <div className="exec-totals">
                <div className="exec-total-item">
                  <span className="exec-total-num">{stats.totalRuns}</span>
                  <span className="exec-total-lbl">Total Runs</span>
                </div>
                <div className="exec-total-item">
                  <span className="exec-total-num">{stats.completedRuns}</span>
                  <span className="exec-total-lbl">Completed</span>
                </div>
                <div className="exec-total-item">
                  <span className="exec-total-num">{stats.totalRuns - stats.completedRuns}</span>
                  <span className="exec-total-lbl">In Progress / Pending</span>
                </div>
              </div>
            </div>
          </div>

          {/* Historical Activity Report */}
          <div className="dash-card" style={{ marginBottom: '1.25rem' }}>
            <div className="dash-card-header">
              <div>
                <h3>Historical Activity Report</h3>
                <p>Long-term test execution velocity and trends</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['monthly', 'overall', '12 months'] as const).map((opt, i) => (
                  <button key={i}
                    className={`dash-btn-outline`}
                    style={{ padding: '4px 12px', fontSize: '0.75rem', minWidth: 'unset',
                      background: (opt === 'monthly' && actPeriod === 'monthly') || (opt === 'overall' && actView === 'overall') ? '#0f2d6b' : 'white',
                      color: (opt === 'monthly' && actPeriod === 'monthly') || (opt === 'overall' && actView === 'overall') ? 'white' : '#374151',
                      border: '1.5px solid #e5e7eb' }}
                    onClick={() => {
                      if (opt === 'monthly') setActPeriod('monthly');
                      else if (opt === 'overall') setActView('overall');
                      else setActMonths(12);
                    }}>
                    {opt === 'monthly' ? 'Monthly' : opt === 'overall' ? 'Overall' : '12 months'}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1.5rem' }}>
              {/* Controls + table */}
              <div style={{ flex: 1 }}>
                <div className="act-controls">
                  <div className="act-control-group">
                    <label>Period</label>
                    <select value={actPeriod} onChange={e => setActPeriod(e.target.value as any)} className="act-select">
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div className="act-control-group">
                    <label>View</label>
                    <select value={actView} onChange={e => setActView(e.target.value as any)} className="act-select">
                      <option value="overall">Overall</option>
                      <option value="by_user">By User</option>
                      <option value="ai_vs_manual">AI vs Manual</option>
                    </select>
                  </div>
                  <div className="act-control-group">
                    <label>Range</label>
                    <select value={actMonths} onChange={e => setActMonths(Number(e.target.value))} className="act-select">
                      <option value={3}>3 months</option>
                      <option value={6}>6 months</option>
                      <option value={12}>12 months</option>
                      <option value={24}>24 months</option>
                    </select>
                  </div>
                  {(actView === 'by_user' || actView === 'ai_vs_manual') && actUsers.length > 0 && (
                    <div className="act-control-group">
                      <label>User</label>
                      <select value={actUserFilter} onChange={e => setActUserFilter(e.target.value)} className="act-select">
                        <option value="">All</option>
                        {actUsers.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {actLoading ? (
                  <div className="act-loading">Loading...</div>
                ) : actRows.length === 0 ? (
                  <div className="act-empty">No activity data for this period.</div>
                ) : (
                  <div className="act-table-wrap">
                    <table className="act-table">
                      <thead>
                        <tr>
                          <th>Period</th>
                          {(actView === 'by_user' || actView === 'ai_vs_manual') && <th>User</th>}
                          {actView === 'ai_vs_manual' ? (
                            <>
                              <th>TS (AI)</th>
                              <th>TS AI %</th>
                              <th>TS (Manual)</th>
                              <th>TS Manual %</th>
                            </>
                          ) : (
                            <>
                              <th>TC</th>
                              <th>TC Executed</th>
                              <th>TS</th>
                              <th>TS Executed</th>
                              <th>Runs Created</th>
                              <th>Runs Completed</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {actRows.filter(r => !actUserFilter || r.user === actUserFilter).map((row, i) => (
                          <tr key={i}>
                            <td className="act-period">{row.period}</td>
                            {(actView === 'by_user' || actView === 'ai_vs_manual') && (
                              <td className="act-user">{row.user}</td>
                            )}
                            {actView === 'ai_vs_manual' ? (
                              <>
                                <td><span className="act-num act-tc">{row.sc_ai ?? 0}</span></td>
                                <td>
                                  <span className="act-pct-badge act-pct-ai">
                                    {row.sc_ai_pct ?? 0}%
                                  </span>
                                </td>
                                <td><span className="act-num act-sc">{row.sc_manual ?? 0}</span></td>
                                <td>
                                  <span className="act-pct-badge act-pct-manual">
                                    {row.sc_manual_pct ?? 0}%
                                  </span>
                                </td>
                              </>
                            ) : (
                              <>
                                <td><span className="act-num act-tc">{row.tc_created ?? 0}</span></td>
                                <td><span className="act-num act-tc">{row.tc_executed ?? 0}</span></td>
                                <td><span className="act-num act-sc">{row.sc_created ?? 0}</span></td>
                                <td><span className="act-num act-sc">{row.sc_executed ?? 0}</span></td>
                                <td><span className="act-num act-run">{row.runs_created ?? 0}</span></td>
                                <td><span className="act-num act-run">{row.runs_completed ?? 0}</span></td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Download CSV */}
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minWidth: 180 }}>
                <button className={`dash-btn-primary${!isAdmin ? ' dash-btn-disabled' : ''}`}
                  onClick={handleExport} disabled={!isAdmin || exportLoading}
                  style={{ width: '100%', justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Download CSV Full Report
                </button>
              </div>
            </div>
          </div>

          {/* Bottom row: Recent Test Runs + Quick Stats */}
          <div className="dash-bottom-row">
            <div className="dash-card dash-card-wide">
              <div className="dash-card-header">
                <div>
                  <h3>Recent Test Runs</h3>
                </div>
                <Link to="/test-runs" className="dash-view-all">View All Activity Reports</Link>
              </div>
              {recentRuns.length === 0 ? (
                <div className="exec-empty">No test runs yet. <Link to="/test-runs/create">Create your first test run</Link>.</div>
              ) : (
                <table className="runs-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Test Run Name</th>
                      <th>Status</th>
                      <th>Success Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRuns.map(run => (
                      <tr key={run.id}>
                        <td className="run-duration">{new Date(run.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                        <td>
                          <Link to={`/test-runs/${run.id}`} className="run-name">{run.name}</Link>
                        </td>
                        <td>{statusBadge(run.status, run.pass_percentage)}</td>
                        <td>
                          <span className={run.pass_percentage < 80 ? 'run-rate-fail' : 'run-rate-pass'}>
                            {run.pass_percentage != null ? `${Math.round(run.pass_percentage * 10) / 10}%` : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="dash-card">
              <div className="dash-card-header"><h3>Quick Stats</h3></div>
              <div className="quick-stats">
                {[
                  { label: 'Projects', value: stats.totalProjects, icon: '📁', link: '/' },
                  { label: 'Test Cases', value: stats.totalTestCases, icon: '✅', link: '/test-cases' },
                  { label: 'Test Scenarios', value: stats.totalScenarios, icon: '🔬', link: '/test-scenarios' },
                  { label: 'Test Runs', value: stats.totalRuns, icon: '▶️', link: '/test-runs' },
                ].map(item => (
                  <Link key={item.label} to={item.link} className="quick-stat-item">
                    <span className="quick-stat-icon">{item.icon}</span>
                    <div className="quick-stat-info">
                      <span className="quick-stat-value">{item.value.toLocaleString()}</span>
                      <span className="quick-stat-label">{item.label}</span>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

};
